import { defineStore } from 'pinia'
import {
  getRutinas,
  crearRutina,
  actualizarRutina,
  eliminarRutina,
  agregarActividadRutina,
  eliminarActividadRutina,
  agregarRefaccionRutina,
  eliminarRefaccionRutina,
} from '@/utils/api_backend'

// ── Estado inicial del borrador ─────────────────────────────
const borradorVacio = () => ({
  nombre:                  '',
  area:                    null,
  periodicidad:            '',
  duracion_estimada_horas: '',
  descripcion:             '',
})

export const useRutinaStore = defineStore('rutina', {
  state: () => ({
    // ── Lista principal ──────────────────────────────────────
    rutinas:    [],
    cargando:   false,
    guardando:  false,

    // ── Borrador ─────────────────────────────────────────────
    modoEdicion:    false,
    rutinaEditandoId: null,
    borrador:         borradorVacio(),

    // Actividades en memoria
    // { _tempId, actividad (id), actividad_nombre, orden, _bdId (si ya existe en BD) }
    borradorActividades: [],

    // Refacciones en memoria
    // { _tempId, refaccion (id), refaccion_nombre, en_almacen, cantidad, _bdId }
    borradorRefacciones: [],

    // IDs de relaciones ya existentes que el usuario eliminó durante edición
    actividadesEliminadas: [],  // [ _bdId ]
    refaccionesEliminadas: [],  // [ _bdId ]

    // ── Errores parciales ────────────────────────────────────
    erroresParciales: [],
  }),

  getters: {
    totalActividades: (state) => state.borradorActividades.length,
    totalRefacciones: (state) => state.borradorRefacciones.length,

    actividadesOrdenadas: (state) =>
      [...state.borradorActividades].sort((a, b) => a.orden - b.orden),
  },

  actions: {
    // ── Cargar lista ─────────────────────────────────────────
    async cargarRutinas(params = {}) {
      this.cargando = true
      try {
        this.rutinas = await getRutinas(params)
      } finally {
        this.cargando = false
      }
    },

    // ── Iniciar borrador nuevo ────────────────────────────────
    iniciarCreacion() {
      this.modoEdicion         = false
      this.rutinaEditandoId    = null
      this.borrador            = borradorVacio()
      this.borradorActividades = []
      this.borradorRefacciones = []
      this.actividadesEliminadas = []
      this.refaccionesEliminadas = []
      this.erroresParciales    = []
    },

    // ── Cargar rutina existente al borrador ───────────────────
    iniciarEdicion(rutina) {
      this.modoEdicion      = true
      this.rutinaEditandoId = rutina.id
      this.erroresParciales = []
      this.actividadesEliminadas = []
      this.refaccionesEliminadas = []

      this.borrador = {
        nombre:                  rutina.nombre,
        area:                    rutina.area ?? null,
        periodicidad:            rutina.periodicidad,
        duracion_estimada_horas: rutina.duracion_estimada_horas ?? '',
        descripcion:             rutina.descripcion ?? '',
      }

      // Cargar actividades existentes (tienen _bdId = id de RutinaActividad)
      this.borradorActividades = (rutina.rutina_actividades || []).map((ra) => ({
        _tempId:          ra.id,
        _bdId:            ra.id,          // ya existe en BD
        actividad:        ra.actividad,
        actividad_nombre: ra.actividad_nombre,
        orden:            ra.orden,
      }))

      // Cargar refacciones existentes
      this.borradorRefacciones = (rutina.rutina_refacciones || []).map((rr) => ({
        _tempId:          rr.id,
        _bdId:            rr.id,
        refaccion:        rr.refaccion,
        refaccion_nombre: rr.refaccion_nombre,
        cantidad:         rr.cantidad,
      }))
    },

    // ── Actividades ───────────────────────────────────────────
    agregarActividad(actividadId, actividadNombre) {
      // Evitar duplicados
      const yaExiste = this.borradorActividades.some(a => a.actividad === actividadId)
      if (yaExiste) return { error: 'Esta actividad ya fue agregada a la rutina.' }

      const orden = this.borradorActividades.length + 1
      this.borradorActividades.push({
        _tempId:          `temp_${Date.now()}`,
        _bdId:            null,   // aún no está en BD
        actividad:        actividadId,
        actividad_nombre: actividadNombre,
        orden,
      })
      return { ok: true }
    },

    quitarActividad(tempId) {
      const idx = this.borradorActividades.findIndex(a => a._tempId === tempId)
      if (idx === -1) return

      const item = this.borradorActividades[idx]
      // Si ya existe en BD, marcarlo para eliminar en el guardado
      if (item._bdId) this.actividadesEliminadas.push(item._bdId)

      this.borradorActividades.splice(idx, 1)
      // Reordenar el campo orden
      this.borradorActividades.forEach((a, i) => { a.orden = i + 1 })
    },

    // ── Refacciones ───────────────────────────────────────────
    agregarRefaccion(refaccionId, refaccionNombre, cantidad) {
      const yaExiste = this.borradorRefacciones.some(r => r.refaccion === refaccionId)
      if (yaExiste) return { error: 'Esta refacción ya fue agregada a la rutina.' }
      if (!cantidad || cantidad < 1) return { error: 'La cantidad debe ser al menos 1.' }

      this.borradorRefacciones.push({
        _tempId:          `temp_${Date.now()}`,
        _bdId:            null,
        refaccion:        refaccionId,
        refaccion_nombre: refaccionNombre,
        cantidad:         parseInt(cantidad),
      })
      return { ok: true }
    },

    quitarRefaccion(tempId) {
      const idx = this.borradorRefacciones.findIndex(r => r._tempId === tempId)
      if (idx === -1) return

      const item = this.borradorRefacciones[idx]
      if (item._bdId) this.refaccionesEliminadas.push(item._bdId)

      this.borradorRefacciones.splice(idx, 1)
    },

    // ── Guardar todo ──────────────────────────────────────────
    async guardarTodo() {
      this.guardando      = true
      this.erroresParciales = []

      try {
        let rutinaId = this.rutinaEditandoId

        // ── PASO 1: Rutina base ────────────────────────────────
        const payload = {
          nombre:                  this.borrador.nombre,
          area:                    this.borrador.area || null,
          periodicidad:            this.borrador.periodicidad,
          duracion_estimada_horas: this.borrador.duracion_estimada_horas || null,
          descripcion:             this.borrador.descripcion,
        }

        if (this.modoEdicion) {
          await actualizarRutina(rutinaId, payload)
        } else {
          const creada = await crearRutina(payload)
          rutinaId = creada.id
        }

        // ── PASO 2: Eliminar actividades removidas (solo edición) ──
        for (const bdId of this.actividadesEliminadas) {
          try {
            await eliminarActividadRutina(rutinaId, bdId)
          } catch {
            this.erroresParciales.push(`No se pudo eliminar la actividad #${bdId}`)
          }
        }

        // ── PASO 3: Agregar actividades nuevas (sin _bdId) ────────
        const actividadesNuevas = this.borradorActividades.filter(a => !a._bdId)
        for (const act of actividadesNuevas) {
          try {
            await agregarActividadRutina(rutinaId, {
              actividad: act.actividad,
              orden:     act.orden,
            })
          } catch {
            this.erroresParciales.push(`No se pudo agregar la actividad "${act.actividad_nombre}"`)
          }
        }

        // ── PASO 4: Eliminar refacciones removidas (solo edición) ──
        for (const bdId of this.refaccionesEliminadas) {
          try {
            await eliminarRefaccionRutina(rutinaId, bdId)
          } catch {
            this.erroresParciales.push(`No se pudo eliminar la refacción #${bdId}`)
          }
        }

        // ── PASO 5: Agregar refacciones nuevas (sin _bdId) ────────
        const refaccionesNuevas = this.borradorRefacciones.filter(r => !r._bdId)
        for (const ref of refaccionesNuevas) {
          try {
            await agregarRefaccionRutina(rutinaId, {
              refaccion: ref.refaccion,
              cantidad:  ref.cantidad,
            })
          } catch {
            this.erroresParciales.push(`No se pudo agregar la refacción "${ref.refaccion_nombre}"`)
          }
        }

        // Recargar lista con datos frescos del servidor
        await this.cargarRutinas()

        return {
          ok:              true,
          erroresParciales: this.erroresParciales,
        }

      } catch (err) {
        // Error en paso 1 → fallo total, nada se guardó
        const msg = err?.response?.data
          ? Object.values(err.response.data).flat().join(' ')
          : 'No se pudo guardar la rutina. Verifica los datos e intenta de nuevo.'
        return { ok: false, error: msg }

      } finally {
        this.guardando = false
      }
    },

    // ── Eliminar rutina (soft delete) ─────────────────────────
    async eliminarRutina(id) {
      await eliminarRutina(id)
      await this.cargarRutinas()
    },
  },
})